import { useEffect, useRef } from 'react';

/**
 * Custom Hook: Adaptive Polling with request deduplication
 * 
 * - FAST (800ms): During active battle/circle (high reactivity)
 * - SLOW (3000ms): When idle/waiting (save bandwidth)
 * - Deduplication: Won't fire a new request if previous is still pending
 * - Error resilience: Backs off on consecutive errors
 */
const useAdaptivePolling = (callback, isActive) => {
  const intervalRef = useRef(null);
  const isPendingRef = useRef(false);
  const errorCountRef = useRef(0);
  const callbackRef = useRef(callback);

  // Keep callback ref fresh without re-triggering effect
  callbackRef.current = callback;

  const FAST_POLL = 800;
  const SLOW_POLL = 3000;
  const ERROR_BACKOFF_MAX = 10000;

  useEffect(() => {
    const basePoll = isActive ? FAST_POLL : SLOW_POLL;

    const executePoll = async () => {
      // Skip if previous request still in flight
      if (isPendingRef.current) return;

      isPendingRef.current = true;
      try {
        await callbackRef.current();
        errorCountRef.current = 0;
      } catch {
        errorCountRef.current = Math.min(errorCountRef.current + 1, 5);
      } finally {
        isPendingRef.current = false;
      }
    };

    // Initial call
    executePoll();

    // Adaptive interval with error backoff
    const getInterval = () => {
      const backoff = errorCountRef.current * 2000;
      return Math.min(basePoll + backoff, ERROR_BACKOFF_MAX);
    };

    // Use dynamic interval via recursive setTimeout for better adaptivity
    const scheduleNext = () => {
      intervalRef.current = setTimeout(() => {
        executePoll().then(() => {
          scheduleNext();
        });
      }, getInterval());
    };

    scheduleNext();

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [isActive]);

  return null;
};

export default useAdaptivePolling;
